import { Link } from 'react-router-dom';
import { TypeAnimation } from 'react-type-animation';

export function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="BeanGo" className="h-8 w-auto" />
              <span className="font-bold text-xl text-foreground">Bean<span className="text-primary">Go</span></span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-8">
            <img src="/logo.png" alt="BeanGo" className="h-24 w-auto mx-auto mb-6" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            AI-Powered Bookkeeping That
            <br />
            <span className="text-primary">Syncs With Your{' '}
              <TypeAnimation
                sequence={['Store', 2000, 'App', 2000, 'Business', 2000]}
                wrapper="span"
                speed={50}
                repeat={Infinity}
                className="inline-block"
              />
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Direct Shopify integration with intelligent automation that learns your business.
            Replace manual bookkeeping with AI that categorizes, matches, and reports for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-primary text-primary-foreground px-8 py-3 rounded-lg text-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Start Free Trial (14 days)
            </Link>
          </div>
          <p className="text-sm text-muted-foreground mt-4">No credit card required</p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-foreground mb-12">
            Everything You Need to Run Your Books
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon="bi-shop"
              title="Shopify Integration"
              description="Auto-import orders, payouts, and fees. Your store data syncs daily."
            />
            <FeatureCard
              icon="bi-robot"
              title="AI Reconciliation"
              description="Smart transaction matching that learns your business patterns."
            />
            <FeatureCard
              icon="bi-graph-up-arrow"
              title="Real-time Dashboards"
              description="P&L, Balance Sheet, and Tax Summary at a glance."
            />
            <FeatureCard
              icon="bi-bank2"
              title="Bank Reconciliation"
              description="Auto-match transactions to bank records."
            />
            <FeatureCard
              icon="bi-box-seam"
              title="Inventory Tracking"
              description="COGS and stock levels synced with your store."
            />
            <FeatureCard
              icon="bi-plug"
              title="MCP Integration"
              description="Connect AI assistants like Claude directly to your books."
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-foreground mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-center text-muted-foreground mb-12">
            All plans include a 14-day free trial
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            <PricingCard
              name="Free"
              price="$0"
              period="/mo"
              features={[
                '1 User',
                '100 Transactions/month',
                'Shopify Sync',
                'Tax Tracking',
              ]}
              buttonText="Get Started"
            />
            <PricingCard
              name="Starter"
              price="$29"
              period="/mo"
              features={[
                '1 User',
                '500 Transactions/month',
                'Shopify Sync',
                'Basic Reports',
                'Tax Tracking',
                'Inventory Tracking',
              ]}
              buttonText="Get Started"
            />
            <PricingCard
              name="Business"
              price="$79"
              period="/mo"
              featured
              features={[
                '5 Users',
                'Unlimited Transactions',
                'Shopify Sync',
                'Advanced Reports',
                'Inventory Tracking',
                'Multi-currency',
              ]}
              buttonText="Get Started"
            />
            <PricingCard
              name="Enterprise"
              price="Custom"
              period=""
              features={[
                'Unlimited Users',
                'Unlimited Transactions',
                'Priority Support',
                'Custom Integrations',
                'Dedicated Manager',
              ]}
              buttonText="Contact Sales"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-foreground mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <FAQItem
              question="How does Shopify integration work?"
              answer="Connect your store in one click. Orders, payouts, and fees automatically sync to BeanGo daily. No manual data entry needed."
            />
            <FAQItem
              question="Do I need to be an accountant to use BeanGo?"
              answer="No! BeanGo is designed for small business owners. The AI handles categorization while you focus on your business."
            />
            <FAQItem
              question="Can I import my QuickBooks data?"
              answer="Yes! We support importing from QuickBooks, Xero, and CSV files."
            />
            <FAQItem
              question="Is my data secure?"
              answer="Absolutely. We use bank-level encryption and your data is stored securely."
            />
            <FAQItem
              question="What taxes does BeanGo support?"
              answer="US sales tax and Canadian GST/HST/PST/QST - with automatic tax reports for easy filing."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to simplify your bookkeeping?
          </h2>
          <p className="text-muted-foreground mb-8">
            Start your free trial today. No credit card required.
          </p>
          <Link
            to="/register"
            className="inline-block bg-primary text-primary-foreground px-8 py-3 rounded-lg text-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            Start Your Free Trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-4 sm:px-6 lg:px-8 bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="BeanGo" className="h-6 w-auto" />
                <span className="font-bold text-foreground">Bean<span className="text-primary">Go</span></span>
              </div>
              <p className="text-sm text-muted-foreground">
                AI-powered bookkeeping for modern businesses.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/features" className="hover:text-foreground">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-foreground">Pricing</Link></li>
                <li><Link to="/roadmap" className="hover:text-foreground">Roadmap</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/about" className="hover:text-foreground">About</Link></li>
                <li><Link to="/blog" className="hover:text-foreground">Blog</Link></li>
                <li><Link to="/contact" className="hover:text-foreground">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-foreground">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            © 2026 BeanGo Inc. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  // Check if icon is an emoji (single character that's not a normal char)
  const isEmoji = icon.length <= 2 && /\p{Emoji}/u.test(icon);
  
  return (
    <div className="bg-card p-6 rounded-lg border border-border">
      <div className="text-4xl mb-4">
        {isEmoji ? icon : <i className={`bi ${icon} text-primary text-4xl`}></i>}
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </div>
  );
}

function PricingCard({
  name,
  price,
  period,
  features,
  buttonText,
  featured = false,
}: {
  name: string;
  price: string;
  period: string;
  features: string[];
  buttonText: string;
  featured?: boolean;
}) {
  return (
    <div className={`bg-card p-6 rounded-lg border ${featured ? 'border-primary' : 'border-border'} relative`}>
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-sm px-3 py-1 rounded-full">
          Most Popular
        </div>
      )}
      <h3 className="text-xl font-semibold text-foreground mb-2">{name}</h3>
      <div className="mb-6">
        <span className="text-4xl font-bold text-foreground">{price}</span>
        <span className="text-muted-foreground">{period}</span>
      </div>
      <ul className="space-y-3 mb-6">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-muted-foreground">
            <span className="text-primary">✓</span>
            {feature}
          </li>
        ))}
      </ul>
      <Link
        to="/register"
        className={`block text-center py-2 rounded-lg transition-colors ${
          featured
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-foreground hover:bg-muted/80'
        }`}
      >
        {buttonText}
      </Link>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  return (
    <details className="group bg-card rounded-lg border border-border">
      <summary className="flex justify-between items-center cursor-pointer p-4 font-semibold text-foreground">
        {question}
        <span className="text-primary group-open:rotate-180 transition-transform">▼</span>
      </summary>
      <div className="px-4 pb-4 text-muted-foreground">
        {answer}
      </div>
    </details>
  );
}